import React, { useRef, useState } from 'react';
import {
  IconGripVertical,
  IconPencil,
  IconChevronDown,
  IconPhoneIncoming,
  IconPhoneOutgoing,
  IconBuilding,
  IconPackage,
  IconDotsVertical,
  IconTrash,
} from '@tabler/icons-react';
import type { PriceLine } from './workItemToRow';
import type { StatusOption } from '../workitems/utils/workItemStatusConfig';
import { ActionPicker, ActionMenu } from '../dropdown';
import { STATUS_PICKER_OPTIONS, statusDropdownDvValue } from './statusPickerOptions';
import './JobRow.css';

/** Status keys drive the stripe, dot, and status-text modifier classes. */
export type JobStatusKey =
  | 'new'
  | 'diagnostics'
  | 'in-progress'
  | 'resolved'
  | 'parts'
  | 'customer'
  | 'third-party'
  | 'supplier'
  | 'payment';

interface JobRowProps {
  ticket: string;
  /** When provided the ticket number renders as an external link. */
  ticketHref?: string;
  date: string;
  customer: string;
  /** Full "Business - Full Name" hover text shown on the customer name. */
  customerTitle?: string;
  /** Shows a building icon before the customer name for business customers. */
  isBusiness?: boolean;
  title: string;
  /** Secondary line under the title — e.g. most recent job-log activity. */
  subtitle?: string;
  /** Most recent phone call, shown under the customer name. */
  lastCall?: { direction: 'inbound' | 'outbound'; time: string };
  /** Task-list progress chip label, e.g. "Tasks (2 of 5)". */
  taskLabel?: string;
  /** True when all tasks are closed (green chip) vs outstanding (amber). */
  taskComplete?: boolean;
  /** Device out with the customer awaiting parts — the chip's hover text. */
  deviceWithCustomer?: string;
  /** Parts chip: non-cancelled part count + how many are received. */
  partsCount?: number;
  partsReceived?: number;
  /** Drives the stripe/dot/text colour. */
  status: JobStatusKey;
  /** Human-readable status label, e.g. "New". */
  statusLabel: string;
  /** Current status in the StatusDropdown value space (highlights the active row). */
  statusValue?: string;
  /** One line per billing segment (paid lines render green). */
  priceLines: PriceLine[];
  onEdit?: () => void;
  /**
   * Request deletion of this job. When set, a "···" overflow menu renders as the
   * last header control (before the chevron) with a "Delete job" action; the
   * dashboard opens the JobDeleteDialog, which is the confirmation.
   */
  onDelete?: () => void;
  onExpand?: () => void;
  /** Rotates the chevron to indicate the row's expansion panel is open. */
  expanded?: boolean;
  /** ── Optional drag-and-drop wiring (set by SortableRow) ── */
  /** Ref for the draggable node — goes on the card. */
  setNodeRef?: (el: HTMLElement | null) => void;
  /** Ref for the drag activator — goes on the handle. */
  setActivatorNodeRef?: (el: HTMLElement | null) => void;
  /** Listeners + attributes for the drag handle. */
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  /** Right-click the drag handle (opens the reassign menu). */
  onHandleContextMenu?: (e: React.MouseEvent) => void;
  /** Transform/transition style applied to the card while sorting. */
  style?: React.CSSProperties;
  /** Omit the built-in drag handle (the parent renders it externally). */
  hideHandle?: boolean;
  /** When set (with statusOptions), the status pill opens a status-change menu. */
  onStatusChange?: (dvValue: string) => void;
  /** Status options for the change menu (from getStatusOptionsForType). */
  statusOptions?: StatusOption[];
}

/**
 * A single job row: drag handle + a status-striped card with ticket meta,
 * customer, title, status pill, price, and expand chevron. Presentational.
 */
export const JobRow: React.FC<JobRowProps> = ({
  ticket,
  ticketHref,
  deviceWithCustomer,
  date,
  customer,
  customerTitle,
  isBusiness,
  title,
  subtitle,
  lastCall,
  taskLabel,
  taskComplete,
  partsCount,
  partsReceived,
  status,
  statusLabel,
  statusValue,
  priceLines,
  onEdit,
  onDelete,
  onExpand,
  expanded,
  setNodeRef,
  setActivatorNodeRef,
  dragHandleProps,
  onHandleContextMenu,
  style,
  hideHandle,
  onStatusChange,
  statusOptions,
}) => {
  const statusRef = useRef<HTMLSpanElement>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const statusClickable = Boolean(onStatusChange && statusOptions);
  // "···" overflow menu (low-frequency job actions — currently just Delete).
  const overflowRef = useRef<HTMLButtonElement>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  return (
  <div className="job-row">
    {!hideHandle && (
      <div className="job-row__handle" ref={setActivatorNodeRef} {...dragHandleProps} onContextMenu={onHandleContextMenu}>
        <IconGripVertical size={18} stroke={1.5} />
      </div>
    )}
    <div
      className="job-row__card"
      ref={setNodeRef}
      style={onExpand ? { ...style, cursor: 'pointer' } : style}
      onClick={onExpand}
    >
      <div className="job-row__stripe" />
      <div className="job-row__inner">
        <div className="job-row__meta">
          {ticketHref ? (
            <a
              className="job-row__ticket"
              href={ticketHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {ticket}
            </a>
          ) : (
            <span className="job-row__ticket">{ticket}</span>
          )}
          <span className="job-row__date">{date}</span>
        </div>
        <div className="job-row__custblock">
          <div className="job-row__custname">
            {isBusiness && (
              <IconBuilding className="job-row__business-icon" size={13} stroke={1.5} />
            )}
            <span
              className="job-row__customer"
              // Hover shows the full "Business - Full Name" form; falls back to
              // the displayed name when there's no extra detail to surface.
              title={customerTitle ?? customer}
            >
              {customer}
            </span>
          </div>
          {lastCall && (
            <span className="job-row__lastcall">
              {lastCall.direction === 'inbound'
                ? <IconPhoneIncoming size={12} stroke={1.5} />
                : <IconPhoneOutgoing size={12} stroke={1.5} />}
              {lastCall.time}
            </span>
          )}
        </div>
        <div className="job-row__titleblock">
          <span className="job-row__title">{title}</span>
          {subtitle && <span className="job-row__subtitle">{subtitle}</span>}
        </div>
        <div className="job-row__endblock">
          <div className="job-row__endtop">
            {partsCount != null && partsCount > 0 && (
              <div
                className="parts-chip"
                title={`${partsReceived ?? 0} of ${partsCount} part${partsCount > 1 ? 's' : ''} received`}
              >
                <IconPackage size={13} stroke={1.5} />
                <span className="parts-chip__badge">{partsCount}</span>
              </div>
            )}
            {deviceWithCustomer && (
              <span className="job-row__taskchip job-row__devicechip" title={deviceWithCustomer}>
                Device w/ Customer
              </span>
            )}
            {taskLabel && (
              <span className={`job-row__taskchip job-row__taskchip--${taskComplete ? 'ok' : 'pending'}`}>
                {taskLabel}
              </span>
            )}
            <span
              ref={statusRef}
              className={`job-row__status job-row__status--${status}`}
              onClick={statusClickable ? (e) => { e.stopPropagation(); setStatusOpen((o) => !o); } : undefined}
              style={statusClickable ? { cursor: 'pointer' } : undefined}
            >
              <span className={`job-row__status-dot job-row__status-dot--${status}`} />
              {statusLabel}
            </span>
            <IconPencil
              className="job-row__edit-icon"
              size={16}
              stroke={1.5}
              onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            />
          </div>
          <div className="job-row__price">
            {priceLines.map((line, i) => (
              <span
                key={i}
                className={`job-row__price-line${line.paid ? ' job-row__price-line--paid' : ''}`}
              >
                {line.amount}
                {line.label && <span className="job-row__price-label"> ({line.label})</span>}
              </span>
            ))}
          </div>
        </div>
        {onDelete && (
          <button
            type="button"
            ref={overflowRef}
            className="job-row__overflow-trigger"
            title="More actions"
            onClick={(e) => { e.stopPropagation(); setOverflowOpen((o) => !o); }}
          >
            <IconDotsVertical size={16} stroke={1.5} />
          </button>
        )}
        <IconChevronDown
          className="job-row__chevron"
          size={20}
          stroke={1.5}
          style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s ease' }}
        />
      </div>
    </div>
    {statusClickable && statusOpen && (
      <ActionPicker
        options={STATUS_PICKER_OPTIONS}
        currentValue={statusValue ?? ''}
        anchorRef={statusRef}
        onClose={() => setStatusOpen(false)}
        onSelect={(value) => {
          // Map the chosen option to its Dynamics value and fire the existing
          // status-update API; skip when it's already the current status.
          if (value === statusValue) return;
          const dvValue = statusDropdownDvValue(value);
          if (dvValue) onStatusChange?.(dvValue);
        }}
      />
    )}
    {onDelete && overflowOpen && (
      <ActionMenu
        anchorRef={overflowRef}
        preferAbove
        onClose={() => setOverflowOpen(false)}
        options={[
          {
            label: 'Delete job',
            Icon: IconTrash,
            destructive: true,
            onSelect: onDelete,
          },
        ]}
      />
    )}
  </div>
  );
};
